import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Premium "Watch Demo" modal that plays an embedded YouTube video
 * inside the website. Used by Hero's "Watch 2-min demo" CTA.
 *
 * Rendered in a React portal mounted to <body> to avoid transformed
 * ancestors (framer-motion parents in the Hero) from hijacking
 * `position: fixed` and stretching the overlay to the page scrollHeight.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   embedUrl: string  (e.g. https://www.youtube.com/embed/abc123)
 *   youtubeUrl: string (optional human URL — only used to derive embed if embedUrl missing)
 *   autoplay: boolean (default true)
 *   title: string (a11y title)
 */

export function youtubeIdFromUrl(url = "") {
  if (!url) return "";
  const e = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
  if (e) return e[1];
  const m = url.match(/(?:youtu\.be\/|v=|youtube\.com\/shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
}

function resolveEmbedSrc(embedUrl, youtubeUrl, autoplay) {
  let src = embedUrl;
  if (!src) {
    const id = youtubeIdFromUrl(youtubeUrl);
    if (id) src = `https://www.youtube.com/embed/${id}`;
  }
  if (!src) return "";
  const u = new URL(src);
  if (autoplay) u.searchParams.set("autoplay", "1");
  u.searchParams.set("rel", "0");
  u.searchParams.set("modestbranding", "1");
  u.searchParams.set("playsinline", "1");
  return u.toString();
}

export const WatchDemoModal = ({ open, onClose, embedUrl, youtubeUrl, autoplay = true, title = "Product demo" }) => {
  const iframeRef = useRef(null);

  // ESC to close + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const src = open ? resolveEmbedSrc(embedUrl, youtubeUrl, autoplay) : "";

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="watch-demo-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-slate-950/70 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-testid="watch-demo-modal"
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 6 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="relative w-full max-w-4xl rounded-2xl bg-black shadow-2xl ring-1 ring-white/10 overflow-hidden"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="watch-demo-close-btn"
              aria-label="Close demo video"
              className="absolute top-2.5 right-2.5 z-10 h-9 w-9 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center ring-1 ring-white/20 backdrop-blur"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative w-full aspect-video bg-black">
              {src ? (
                <iframe
                  ref={iframeRef}
                  data-testid="watch-demo-iframe"
                  src={src}
                  title={title}
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                  Add a YouTube URL in the CMS to enable this demo video.
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default WatchDemoModal;
